import { useEffect } from 'react';
import { message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { UnknownAction } from 'redux-saga';

import { TicketStatus, TicketStatusTitle, useTicket } from '../../../../store/ticket';
import { ticketUpdate,
  ticketUpdateClear,
  ticketUpdateErrorClear } from '../../../../store/ticket/update';
import { ticketDelete, ticketDeleteClear } from '../../../../store/ticket/delete';
import { useSimpleModal } from '../../../Common/Modal/Simple';

interface UseTicketActionsOptions {
  onDeleted?: () => void;
}

export default function useTicketActions({ onDeleted }: UseTicketActionsOptions = {}) {
  const dispatch = useDispatch();
  const { modal, contextHolder } = useSimpleModal();
  const { update, delete: deleteTicket } = useTicket();

  useEffect(
    () => () => {
      dispatch(ticketUpdateClear() as UnknownAction);
      dispatch(ticketUpdateErrorClear() as UnknownAction);
      dispatch(ticketDeleteClear() as UnknownAction);
    },
    [],
  );

  useEffect(() => {
    if (update.response) {
      message.success('Successfully updated').then(() => dispatch(ticketUpdateClear() as UnknownAction));
    }
  }, [update.response]);

  useEffect(() => {
    if (update.error) {
      message.error(JSON.stringify(update.error.data.message)).then(
        () => dispatch(ticketUpdateErrorClear() as UnknownAction),
      );
    }
  }, [update.error]);

  useEffect(() => {
    if (deleteTicket.response) {
      onDeleted?.();
    }
  }, [deleteTicket.response]);

  useEffect(() => {
    if (deleteTicket.error) {
      message.error(JSON.stringify(deleteTicket.error.data.message));
    }
  }, [deleteTicket.error]);

  const confirmDelete = (ticketId: string) => {
    if (!ticketId) {
      return;
    }

    modal.confirm({
      title: 'Are you sure you want to Delete the ticket?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => dispatch(ticketDelete({ id: ticketId }) as UnknownAction),
      okText: 'Delete',
    });
  };

  const confirmResolve = (ticketId: string) => {
    if (!ticketId) {
      return;
    }

    modal.confirm({
      title: 'Are you sure you want to Resolve the ticket?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => dispatch(ticketUpdate({
        id: ticketId,
        body: { status: TicketStatus.RESOLVED },
      }) as UnknownAction),
      okText: TicketStatusTitle[TicketStatus.RESOLVED],
    });
  };

  const confirmOpen = (ticketId: string) => {
    if (!ticketId) {
      return;
    }

    modal.confirm({
      title: `Are you sure you want to ${TicketStatusTitle[TicketStatus.OPEN]} the ticket?`,
      icon: <ExclamationCircleOutlined />,
      onOk: () => dispatch(ticketUpdate({
        id: ticketId,
        body: { status: TicketStatus.OPEN },
      }) as UnknownAction),
      okText: TicketStatusTitle[TicketStatus.OPEN],
    });
  };

  const confirmToggleStatus = (ticketId: string, status: TicketStatus) => {
    if (status === TicketStatus.OPEN) {
      confirmResolve(ticketId);
    } else {
      confirmOpen(ticketId);
    }
  };

  return {
    contextHolder,
    confirmDelete,
    confirmResolve,
    confirmOpen,
    confirmToggleStatus,
    update,
    deleteTicket,
  };
}
